import type { MatchRound, MatchSessionState, MatchAnswer, MatchSessionResult } from "./types";
import { ScoringSystem } from "./ScoringSystem";

type Listener = (event: string, data: unknown) => void;

/**
 * Match session state machine. No knowledge of ocean, wildlife, or any specific content.
 * Emits events for L4 UI to render — never touches the DOM.
 */
export class MatchSession {
  private state: MatchSessionState;
  private rounds: MatchRound[];
  private listeners = new Set<Listener>();
  private answerStartTs = 0;
  private pendingTimer: ReturnType<typeof setTimeout> | null = null;
  private disposed = false;

  constructor(rounds: MatchRound[]) {
    this.rounds = rounds;
    this.state = {
      phase: "idle",
      currentRoundIdx: 0,
      currentItemIdx: -1,
      answers: [],
      score: 0,
      streak: 0,
      bestStreak: 0,
      correctCount: 0,
      wrongCount: 0
    };
  }

  on(listener: Listener): void {
    this.listeners.add(listener);
  }

  off(listener: Listener): void {
    this.listeners.delete(listener);
  }

  start(): void {
    if (this.state.phase !== "idle") return;
    this.state.phase = "starting";
    this.emit("session:started", null);
    this.showNextItem();
  }

  answer(chosenCategoryId: string): void {
    if (this.state.phase !== "item-shown") return;
    const round = this.rounds[this.state.currentRoundIdx];
    const item = round.items[this.state.currentItemIdx];
    const correct = item.categoryId === chosenCategoryId;
    const elapsedMs = performance.now() - this.answerStartTs;

    const answer: MatchAnswer = {
      itemId: item.id,
      chosenCategoryId,
      correct,
      elapsedMs
    };
    this.state.answers.push(answer);
    this.state.phase = "answered";

    if (correct) {
      this.state.correctCount++;
      this.state.streak++;
      if (this.state.streak > this.state.bestStreak) {
        this.state.bestStreak = this.state.streak;
      }
      this.state.score += ScoringSystem.forCorrect(this.state.streak, elapsedMs);
    } else {
      this.state.wrongCount++;
      this.state.streak = 0;
    }

    this.emit("session:answered", { answer, item, state: { ...this.state } });

    this.pendingTimer = setTimeout(() => {
      this.pendingTimer = null;
      if (this.disposed) return;
      this.showNextItem();
    }, 900);
  }

  dispose(): void {
    this.disposed = true;
    if (this.pendingTimer !== null) {
      clearTimeout(this.pendingTimer);
      this.pendingTimer = null;
    }
    this.listeners.clear();
  }

  getState(): Readonly<MatchSessionState> {
    return this.state;
  }

  getResult(): MatchSessionResult {
    const totalItems = this.rounds.reduce((n, r) => n + r.items.length, 0);
    const uniqueMatched = Array.from(
      new Set(this.state.answers.filter((a) => a.correct).map((a) => a.itemId))
    );
    return {
      totalItems,
      correctCount: this.state.correctCount,
      wrongCount: this.state.wrongCount,
      bestStreak: this.state.bestStreak,
      finalScore: this.state.score,
      itemsMatched: uniqueMatched
    };
  }

  private showNextItem(): void {
    this.state.currentItemIdx++;
    const round = this.rounds[this.state.currentRoundIdx];
    if (this.state.currentItemIdx >= round.items.length) {
      this.state.currentRoundIdx++;
      this.state.currentItemIdx = 0;
      if (!this.rounds.length || this.state.currentRoundIdx >= this.rounds.length) {
        this.state.phase = "complete";
        this.emit("session:complete", this.getResult());
        return;
      }
      this.emit("session:next-round", { roundIdx: this.state.currentRoundIdx });
    }
    const currentRound = this.rounds[this.state.currentRoundIdx];
    const item = currentRound.items[this.state.currentItemIdx];
    this.state.phase = "item-shown";
    this.answerStartTs = performance.now();
    this.emit("session:item-shown", { item, categories: currentRound.categories });
  }

  private emit(event: string, data: unknown): void {
    for (const l of this.listeners) l(event, data);
  }
}
